import { formatAmount, formatDateTime } from "~/common/utils"
import { ReloadOutlined, SearchOutlined, ShopOutlined } from "@ant-design/icons"
import { Button, Flex, Form, Input, InputNumber, Table, Typography } from "antd"
import type { TableProps } from "antd"
import { listDoudianSubscriptionOrders } from "~/api/staff"
import { MOBILE_PHONE_PATTERN } from "~/common/constants"
import { usePagedQuery } from "~/pages/Staff/usePagedQuery"
import styles from "./index.css"

const { Text, Title } = Typography

type OrderFilterFormValues = {
  phone?: string
  shopId?: number
}

export const StaffDoudianOrderList = () => {
  const [form] = Form.useForm<OrderFilterFormValues>()
  const { loading, search, refresh, tableProps } = usePagedQuery<
    API.Staff.DoudianSubscriptionOrderListParams,
    API.Staff.DoudianSubscriptionOrder
  >({
    fetchPage: listDoudianSubscriptionOrders,
    initialFilters: {},
    errorMessage: "抖店订单加载失败，请稍后重试",
  })

  const searchOrders = (values: OrderFilterFormValues) => {
    const phone = values.phone?.trim()

    search({
      ...(values.shopId ? { shop_id: values.shopId } : {}),
      ...(phone ? { phone } : {}),
    })
  }

  const resetSearch = () => {
    form.resetFields()
    search({})
  }

  const columns: TableProps<API.Staff.DoudianSubscriptionOrder>["columns"] = [
    {
      title: "抖店订单ID",
      dataIndex: "order_id",
      width: 280,
      render: (value: string) => (
        <Text
          className={styles.orderId}
          copyable={{ text: value, tooltips: ["复制订单ID", "已复制"] }}
          ellipsis={{ tooltip: value }}
        >
          {value}
        </Text>
      ),
    },
    {
      title: "店铺ID",
      dataIndex: "shop_id",
      width: 150,
    },
    {
      title: "店铺手机号",
      dataIndex: "phone",
      width: 140,
      render: (value: string) => value || "-",
    },
    {
      title: "订购商品",
      width: 240,
      render: (_, record) => record.push_sku_info.title || "-",
    },
    {
      title: "规格",
      width: 150,
      render: (_, record) => record.push_sku_info.spec_value || "-",
    },
    {
      title: "实付金额",
      dataIndex: "pay_amount",
      width: 110,
      render: (value: number) => formatAmount(value),
    },
    {
      title: "支付时间",
      dataIndex: "pay_time",
      width: 180,
      render: (value: number) => formatDateTime(value),
    },
    {
      title: "服务有效期",
      width: 340,
      render: (_, record) =>
        `${formatDateTime(record.service_start_time)} 至 ${formatDateTime(
          record.service_end_time,
        )}`,
    },
  ]

  return (
    <Flex
      className={styles.page}
      vertical
      gap={18}
    >
      <Flex
        vertical
        gap={6}
      >
        <Flex
          align="center"
          gap={8}
        >
          <ShopOutlined className={styles.titleIcon} />
          <Title level={2}>抖店订单列表</Title>
        </Flex>
        <Text type="secondary">
          查看抖店订购支付记录，可按店铺 ID 或店铺手机号筛选。
        </Text>
      </Flex>

      <section className={styles.panel}>
        <Flex
          align="center"
          justify="space-between"
          gap={12}
          wrap
        >
          <Form<OrderFilterFormValues>
            className={styles.searchForm}
            form={form}
            layout="inline"
            onFinish={searchOrders}
          >
            <Form.Item
              label="店铺ID"
              name="shopId"
            >
              <InputNumber
                className={styles.searchInput}
                min={1}
                precision={0}
                placeholder="请输入 shop_id"
              />
            </Form.Item>
            <Form.Item
              label="店铺手机号"
              name="phone"
              rules={[
                {
                  pattern: MOBILE_PHONE_PATTERN,
                  message: "请输入正确的手机号",
                },
              ]}
            >
              <Input
                className={styles.searchInput}
                allowClear
                maxLength={11}
                placeholder="请输入手机号"
              />
            </Form.Item>
            <Form.Item>
              <Flex gap={8}>
                <Button
                  htmlType="submit"
                  icon={<SearchOutlined />}
                  loading={loading}
                  type="primary"
                >
                  查询
                </Button>
                <Button onClick={resetSearch}>重置</Button>
              </Flex>
            </Form.Item>
          </Form>

          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => refresh()}
          >
            刷新
          </Button>
        </Flex>

        <Table
          columns={columns}
          {...tableProps}
          locale={{ emptyText: "暂无抖店订购订单" }}
          rowKey={(record) => record.id || record.order_id}
          scroll={{ x: 1_700 }}
        />
      </section>
    </Flex>
  )
}
